import { useState } from "react";
import dayjs from "dayjs";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import { PieceStatus } from "features/builder/types";
import { ProjectUser } from "features/creator/elements";
import { PiecePopoverMenu } from "features/builder/elements/PiecePopoverMenu";
import { TRASH_RETENTION_DAYS, TRASH_WARNING_DAYS } from "features/builder/mockData";
import { MockPiece, PieceListContext, PieceCardAction } from "features/builder/pieceList";

const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/default_capture.png`;
const DEFAULT_USER_ICON = `${Constants.assetHost}/assets/users/icons/icon_default_kage01-1.png`;

// 1000 以上は 1.2k 表記
const formatCount = (n: number): string => {
  if (n < 1000) return String(n);
  const v = Math.floor(n / 100) / 10;
  return `${v}k`;
};

// deleted_at から自動消去までの残日数
const daysLeft = (deletedAt: string | null): number => {
  if (!deletedAt) return TRASH_RETENTION_DAYS;
  const passed = dayjs().diff(dayjs(deletedAt), "day");
  return Math.max(TRASH_RETENTION_DAYS - passed, 0);
};

type CardButton = {
  labelKey: string;
  icon?: string;
  action: PieceCardAction;
  className: string;
  disabled?: boolean;
};

type CardVariant = {
  borderClass: string;
  imageFilterClass: string;
  cardOpacityClass: string;
  statusPill: { labelKey: string; pillClass: string } | null;
  showRibbon: boolean;
  showRemix: boolean;
  showMeta: boolean;
  showOverflow: boolean;
  left: CardButton | null;
  right: CardButton | null;
};

const PLAY_BTN: CardButton = {
  labelKey: "MSG_BUILDER_BTN_PLAY",
  icon: "play_arrow",
  action: PieceCardAction.PLAY,
  className: "btn-secondary",
};

const getCardVariant = (piece: MockPiece, context: PieceListContext): CardVariant => {
  const isRemix = piece.copied_piece_id != null;
  const isOfficial = piece.is_template;

  if (context === PieceListContext.TRASH) {
    return {
      borderClass: "border-[3px] border-brown-80",
      imageFilterClass: "[filter:grayscale(0.6)] opacity-70",
      cardOpacityClass: "opacity-[0.85]",
      statusPill: {
        labelKey: "MSG_BUILDER_CARD_STATUS_REMOVED",
        pillClass: "bg-brown-80",
      },
      showRibbon: false,
      showRemix: false,
      showMeta: true,
      showOverflow: false,
      left: {
        labelKey: "MSG_BUILDER_BTN_RESTORE",
        icon: "undo",
        action: PieceCardAction.RESTORE,
        className: "btn-primary",
      },
      right: {
        labelKey: "MSG_BUILDER_BTN_DELETE_FOREVER",
        icon: "delete_forever",
        action: PieceCardAction.DELETE_FOREVER,
        className: "btn-danger",
      },
    };
  }

  if (context === PieceListContext.PUBLIC) {
    return {
      borderClass: isOfficial
        ? "border-[4px] border-blue-100 shadow-[0_0_12px_rgba(76,157,241,0.4)]"
        : "border-[3px] border-blue-100",
      imageFilterClass: "",
      cardOpacityClass: "",
      statusPill: isOfficial
        ? { labelKey: "MSG_BUILDER_CARD_STATUS_OFFICIAL", pillClass: "bg-blue-100" }
        : { labelKey: "MSG_BUILDER_CARD_STATUS_PUBLISHED", pillClass: "bg-blue-100" },
      showRibbon: isOfficial,
      showRemix: isRemix,
      showMeta: true,
      showOverflow: true,
      left: PLAY_BTN,
      right: {
        labelKey: "MSG_BUILDER_BTN_COPY",
        icon: "cached",
        action: PieceCardAction.COPY,
        className: "btn-purple",
      },
    };
  }

  // PieceListContext.MINE
  switch (piece.status) {
    case PieceStatus.DRAFT_NEW:
      return {
        borderClass: "border-[3px] border-dashed border-[#F6C453]",
        imageFilterClass: "",
        cardOpacityClass: "",
        statusPill: {
          labelKey: "MSG_BUILDER_CARD_STATUS_DRAFT",
          pillClass: "bg-[#F6C453] text-textcolor-black",
        },
        showRibbon: false,
        showRemix: false,
        showMeta: true,
        showOverflow: true,
        left: {
          labelKey: "MSG_BUILDER_BTN_PLAY",
          icon: "play_arrow",
          action: PieceCardAction.PLAY,
          className: "btn-gray",
          disabled: true,
        },
        right: {
          labelKey: "MSG_BUILDER_BTN_CONTINUE",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
    case PieceStatus.EDITING:
      return {
        borderClass: "border-[3px] border-[#9BBFD4]",
        imageFilterClass: "",
        cardOpacityClass: "",
        statusPill: {
          labelKey: "MSG_BUILDER_CARD_STATUS_EDITING",
          pillClass: "bg-[#9BBFD4]",
        },
        showRibbon: false,
        showRemix: isRemix,
        showMeta: true,
        showOverflow: true,
        left: {
          labelKey: "MSG_BUILDER_BTN_PUBLISHED_NOW",
          icon: "play_arrow",
          action: PieceCardAction.PLAY,
          className: "btn-secondary",
        },
        right: {
          labelKey: "MSG_BUILDER_BTN_RESUME",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
    case PieceStatus.UNPUBLISHED:
      return {
        borderClass: "border-[3px] border-[#9CA3AF]",
        imageFilterClass: "[filter:grayscale(0.3)]",
        cardOpacityClass: "",
        statusPill: {
          labelKey: "MSG_BUILDER_CARD_STATUS_UNPUBLISHED",
          pillClass: "bg-[#9CA3AF]",
        },
        showRibbon: false,
        showRemix: isRemix,
        showMeta: true,
        showOverflow: true,
        left: PLAY_BTN,
        right: {
          labelKey: "MSG_BUILDER_MAP_EDIT_BTN",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
    default:
      // PUBLISHED
      return {
        borderClass: "border-[3px] border-green-100",
        imageFilterClass: "",
        cardOpacityClass: "",
        statusPill: {
          labelKey: "MSG_BUILDER_CARD_STATUS_PUBLISHED",
          pillClass: "bg-green-100",
        },
        showRibbon: false,
        showRemix: isRemix,
        showMeta: true,
        showOverflow: true,
        left: PLAY_BTN,
        right: {
          labelKey: "MSG_BUILDER_MAP_EDIT_BTN",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
  }
};

export const MapCard = ({
  piece,
  context,
  onAction,
}: {
  piece: MockPiece;
  context: PieceListContext;
  onAction: (action: PieceCardAction) => void;
}) => {
  const play = usePlaySound();
  const [menuOpen, setMenuOpen] = useState(false);
  const variant = getCardVariant(piece, context);

  const fire = (action: PieceCardAction) => {
    play();
    btnDelay(() => onAction(action));
  };

  const handleCard = () => fire(PieceCardAction.DETAIL);

  const handleButton =
    (action: PieceCardAction, disabled?: boolean) => (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (disabled) return;
      fire(action);
    };

  const handleToggleMenu = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    play();
    setMenuOpen((v) => !v);
  };

  const handleMenuAction = (action: PieceCardAction) => {
    setMenuOpen(false);
    fire(action);
  };

  const left = daysLeft(piece.deleted_at);
  const isUrgent = left <= TRASH_WARNING_DAYS;
  const isEditing =
    context === PieceListContext.MINE &&
    (piece.status === PieceStatus.DRAFT_NEW || piece.status === PieceStatus.EDITING);
  const viewed = isEditing ? 0 : piece.viewed;
  const liked = isEditing ? 0 : piece.liked;
  const played = isEditing ? 0 : piece.played;

  const renderButton = (btn: CardButton) => (
    <button
      type="button"
      disabled={btn.disabled}
      onClick={handleButton(btn.action, btn.disabled)}
      className={`type-inherit-font btn ${btn.className} !flex-1 min-w-0`}
    >
      <div className="flex-row-center gap-1">
        {btn.icon && (
          <span className="material-symbols-outlined text-white type-sm">{btn.icon}</span>
        )}
        <p className="type-text type-sm text-white whitespace-nowrap">{I18n.t(btn.labelKey)}</p>
      </div>
    </button>
  );

  return (
    <div
      onClick={handleCard}
      className={`flex-col-el relative w-[500px] rounded-2xl bg-white border-solid shadow-md pointer-events-auto cursor-pointer ${variant.borderClass} ${variant.cardOpacityClass}`}
    >
      <div className="flex-col-el relative w-full h-[350px] overflow-hidden rounded-t-2xl bg-beige-60">
        {piece.cover || piece.capture ? (
          <img
            alt={piece.name}
            src={piece.cover ?? piece.capture ?? FALLBACK_IMAGE}
            onError={(e) => {
              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
            }}
            className={`w-full h-full object-cover ${variant.imageFilterClass}`}
          />
        ) : (
          <div className={`flex-col-center w-full h-full ${variant.imageFilterClass}`}>
            <p className="type-text type-display">{piece.emoji ?? "🧩"}</p>
          </div>
        )}

        <div className="flex-row-el items-center justify-between gap-3 absolute top-0 left-0 right-0 px-3 py-2 bg-white/70 backdrop-blur-sm z-[3]">
          <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
            <p className="type-text type-body text-textcolor-black !text-left whitespace-nowrap truncate w-full">
              {piece.name}
            </p>
            {variant.statusPill && (
              <div className={`flex-row-center px-2 rounded-full ${variant.statusPill.pillClass}`}>
                <p
                  className={`type-text type-sm whitespace-nowrap ${
                    variant.statusPill.pillClass.includes("text-textcolor-black")
                      ? ""
                      : "text-white"
                  }`}
                >
                  {I18n.t(variant.statusPill.labelKey)}
                </p>
              </div>
            )}
          </div>
          {piece.author_name && (
            <div className="shrink-0">
              <ProjectUser name={piece.author_name} icon={piece.author_icon ?? DEFAULT_USER_ICON} />
            </div>
          )}
        </div>

        {variant.showRibbon && (
          <div className="flex-row-el items-center gap-1 absolute top-24 left-0 px-2 py-1 rounded-br-lg bg-blue-100 z-[3]">
            <span className="material-symbols-outlined text-white type-sm">verified</span>
            <p className="type-text type-sm text-white">
              {I18n.t("MSG_BUILDER_CARD_STATUS_OFFICIAL")}
            </p>
          </div>
        )}

        {variant.showRemix && (
          <div className="flex-row-el items-center gap-1 absolute top-24 right-12 px-2 py-1 rounded-full bg-white z-[2]">
            <span className="material-symbols-outlined text-textcolor-black/70 type-sm">
              cached
            </span>
            <p className="type-text type-sm text-textcolor-black/70">
              {I18n.t("MSG_BUILDER_CARD_BADGE_REMIX")}
            </p>
          </div>
        )}

        {variant.showOverflow && (
          <button
            type="button"
            onClick={handleToggleMenu}
            aria-label="menu"
            className="type-inherit-font flex-col-center absolute top-24 right-2 w-8 h-8 rounded-full bg-white/80 border-0 cursor-pointer p-0 z-[2]"
          >
            <span className="material-symbols-outlined text-textcolor-black type-md">
              more_vert
            </span>
          </button>
        )}

        {variant.showMeta && (
          <div className="flex-row-el absolute bottom-0 left-0 right-0 items-center justify-end gap-3 bg-white/70 backdrop-blur-sm px-3 py-1">
            <div className="flex-row-el items-center gap-1">
              <span className="material-symbols-outlined text-[#4c9df1] type-sm">visibility</span>
              <p className="type-text type-sm text-textcolor-black">{formatCount(viewed)}</p>
            </div>
            <div className="flex-row-el items-center gap-1">
              <span className="material-symbols-outlined material-symbols-filled text-[#fc6560] type-sm">
                favorite
              </span>
              <p className="type-text type-sm text-textcolor-black">{formatCount(liked)}</p>
            </div>
            <div className="flex-row-el items-center gap-1">
              <span className="material-symbols-outlined text-green-100 type-sm">play_arrow</span>
              <p className="type-text type-sm text-textcolor-black">{formatCount(played)}</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex-col-el items-start w-full px-3 pt-2 pb-3 gap-2">
        {context === PieceListContext.PUBLIC && piece.original_author_name && (
          <p className="type-text type-sm text-textcolor-black/70 w-full text-left truncate">
            {I18n.t("MSG_BUILDER_CARD_REMIX_FROM", {
              name: piece.original_author_name,
            })}
          </p>
        )}

        {context === PieceListContext.TRASH && (
          <p
            className={`type-text type-sm w-full text-left truncate ${
              isUrgent ? "text-danger animate-pulse" : "text-textcolor-black/70"
            }`}
          >
            {I18n.t("MSG_BUILDER_TRASH_DAYS_LEFT", { days: left })}
          </p>
        )}

        <div className="flex-row-el justify-center items-center w-full gap-2 upsilon">
          {variant.left && renderButton(variant.left)}
          {variant.right && renderButton(variant.right)}
          {context !== PieceListContext.TRASH && (
            <button
              type="button"
              onClick={handleButton(PieceCardAction.DETAIL)}
              className="type-inherit-font btn btn-primary-outlined !flex-1 min-w-0 !border-2 !border-solid !border-green-100"
            >
              <div className="flex-row-center gap-1">
                <span className="material-symbols-outlined text-green-100 type-sm">read_more</span>
                <p className="type-text type-sm text-green-100 whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_MAP_DETAIL_BTN")}
                </p>
              </div>
            </button>
          )}
        </div>
      </div>

      {menuOpen && (
        <PiecePopoverMenu
          context={context}
          onClose={() => setMenuOpen(false)}
          onAction={handleMenuAction}
        />
      )}
    </div>
  );
};
